import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MonoFieldValueAccessorComponent } from '../mono-field-value-accessor.component';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete
} from '@angular/material/autocomplete';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { SdFormLibelleComponent } from '../form-libelle/form-libelle.component';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';
import { BrandThemesEnum, ThemesEnum } from '../../../_enums/themes.enum';
import { IconSizeEnum } from '../../../_enums/icon.enum';
import { SdButtonComponent } from '../../buttons/button/button.component';
import { SdHtmlComponent } from '../../html/html.component';

@Component({
  selector: 'sd-form-input',
  templateUrl: './form-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SdFormInputComponent)
    },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ],
  standalone: true,
  imports: [
    SdFormLibelleComponent,
    MatInput,
    MatAutocompleteTrigger,
    FormsModule,
    NgClass,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    SdButtonComponent,
    SdHtmlComponent,
    TranslateModule
  ]
})
/**
 * Component for input type text
 */
export class SdFormInputComponent
  extends MonoFieldValueAccessorComponent<string | number>
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  constructor(
    cdRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    @Inject(MAT_AUTOCOMPLETE_DEFAULT_OPTIONS)
    public matAutocompleteDefaultOptions,
    @Inject(PLATFORM_ID) protected platformId: any,
  ) {
    super(cdRef);
  }

  themesEnum = ThemesEnum;
  brandThemesEnum = BrandThemesEnum;
  iconSize = IconSizeEnum;
  inputValueChange: Subscription;
  _suggestions = [];
  selectedSuggestion: any;
  showAddressCompliment: boolean;
  isActive = false;

  @Input()
  set suggestions(value) {
    if (!this.showAddressCompliment) {
      this._suggestions = value;
    }
    this.adjustDisplay();
  }

  @Input()
  isFilled = false;

  @Input()
  delay = 0;

  /**
   * The type to be applied to the input element
   */
  @Input()
  type: 'text' | 'search' | 'password' | 'number' | 'email' | 'date' = 'text';

  @Input()
  step: number;

  /**
   * Default value false, this property, allows to display "*" in the required fields.
   * <b>Important</b> This property does not concern validation
   */
  @Input()
  required = false;

  @Input()
  isFake = false;

  @Input()
  autocomplete: 'off' | 'on' | any;

  /**
   * Allows to limit the number of characters to be entered
   */
  @Input()
  maxlength: number;

  /**
   * Allows to add a custom to the input field (icon or button for example)
   */
  @Input()
  isFormAddon = false;

  /**
   * Default value false, If you use ReactiveForm, it is better to pass a FormControl via the formControlName,
   * the latter must be managed as follows:
   * <br/>
   * disabled = new FormControl({value: 'disabled', disabled: true}, Validators.required);
   *
   */
  @Input()
  set disabled(status: boolean) {
    this.setDisabledState(status);
  }

  @Input()
  validStatus = false;

  /**
   * Defined the theme
   */
  @Input()
  theme: ThemesEnum.light | ThemesEnum.dark;

  @Input()
  min: number;

  @Input()
  max: number;

  @Input()
  isAutoComplete = false;

  @Input() class = '';
  inputValue: string;

  @ViewChild('input')
  input: ElementRef<HTMLInputElement>;

  @ViewChild('trigger')
  trigger: MatAutocompleteTrigger;

  @Output()
  inputChanged = new EventEmitter<string>();

  @Output()
  inputActive = new EventEmitter<boolean>();

  @Output()
  clear = new EventEmitter<void>();

  @Output()
  suggestedAddressSelected = new EventEmitter<string>();

  @Output()
  blurEvent = new EventEmitter<FocusEvent>();

  @Output()
  submitted = new EventEmitter<void>();

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      super.getClasses(),
      this.theme ? 'is-theme-' + this.theme : '',
      this.isFilled ? 'is-filled' : '',
      this.validStatus === false
        ? 'is-not-valid'
        : this.validStatus
          ? 'is-valid'
          : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  static isComplements(selectedSuggestion) {
    if (selectedSuggestion?.complements?.length > 0) {
      return (
        selectedSuggestion?.complements?.length !== 1 ||
        selectedSuggestion?.value !== selectedSuggestion?.complements[0]
      );
    }
    return false;
  }

  ngAfterViewInit() {
    this.inputValueChange = this.internalField.valueChanges
      .pipe(debounceTime(this.delay))
      .subscribe((value) => this.onValueChange(value));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.inputValueChange?.unsubscribe();
  }

  mappingChildToParent(value: string | number): string | number {
    return value;
  }

  mappingParentToChild(value: string | number): string | number {
    return value;
  }

  onValueChange(value: string) {
    if (this.type === 'number') {
      if (this.min && parseInt(value) < this.min) {
        value = this.min.toString();
        this.internalField.setValue(value);
      }
      if (this.max && parseInt(value) > this.max) {
        value = this.max.toString();
        this.internalField.setValue(value);
      }
    }

    this.isFilled = value?.length > 0;
    this.inputChanged.emit(value);
  }

  onFocus() {
    this.isActive = true;
    this.inputActive.emit(this.isActive);
  }

  onBlur($event: FocusEvent) {
    this.propagateTouched();
    this.isActive = false;
    this.blurEvent.emit($event);
  }

  clearInputField(input) {
    input.value = '';
    this.isFilled = false;
    this.clear.emit();
    this.cdRef.markForCheck();
  }

  highlightSubstring(name: string): SafeHtml {
    const searchText = this.input?.nativeElement?.value;
    if (!searchText) {
      return name;
    }
    const regex = new RegExp(searchText, 'gi');
    const highlighted = name.replace(
      regex,
      (match) => `<span class="highlight">${match}</span>`,
    );
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  setInputElementBorderRadius(topLeft, topRight, bottomLeft, bottomRight) {
    const inputElement = this.input?.nativeElement;
    if (inputElement) {
      inputElement.style.borderTopLeftRadius = topLeft;
      inputElement.style.borderTopRightRadius = topRight;
      inputElement.style.borderBottomLeftRadius = bottomLeft;
      inputElement.style.borderBottomRightRadius = bottomRight;
    }
  }

  onOptionSelected($event: MatAutocompleteSelectedEvent) {
    this.suggestedAddressSelected.emit($event.option.id);
    const selectedSuggestion = this._suggestions.find(
      (item) => item.key === $event.option.id,
    );
    this.selectedSuggestion = selectedSuggestion;
    if (SdFormInputComponent.isComplements(selectedSuggestion)) {
      this._suggestions = selectedSuggestion?.complements.map((element) => {
        return {
          key: selectedSuggestion.key,
          value: element
        };
      });

      this.showAddressCompliment = true;
    } else {
      this.showAddressCompliment = false;
    }
  }

  private adjustDisplay() {
    if (isPlatformBrowser(this.platformId)) {
      const className = 'mat-mdc-autocomplete-panel';
      const suggestionsPanelSelector =
        '.mat-mdc-autocomplete-panel.mat-mdc-autocomplete-visible';
      const borderRadius = getComputedStyle(
        document.documentElement,
      ).getPropertyValue('--brand--border-radius-form');
      const observer = new MutationObserver((mutationsList) => {
        const hasSuggestions = this._suggestions?.length > 0;
        const elements = document.querySelector(suggestionsPanelSelector);
        const childElement = document.querySelector(
          '.mat-mdc-autocomplete-panel',
        );
        const parentElement = childElement?.parentElement;

        if (!!elements && hasSuggestions) {
          if (
            parentElement.classList.contains('mat-mdc-autocomplete-panel-above')
          ) {
            this.setInputElementBorderRadius(
              '0',
              '0',
              borderRadius,
              borderRadius,
            );
          } else {
            this.setInputElementBorderRadius(
              borderRadius,
              borderRadius,
              '0',
              '0',
            );
          }
        } else {
          this.setInputElementBorderRadius(
            borderRadius,
            borderRadius,
            borderRadius,
            borderRadius,
          );
        }

        const removedNodes = mutationsList.reduce((acc, mutation) => {
          return acc.concat(Array.from(mutation.removedNodes));
        }, []);
        const hasRemovedPanel = removedNodes.some(
          (node) =>
            node instanceof Element && node.classList.contains(className),
        );

        if (hasRemovedPanel && !elements && this.input) {
          this.setInputElementBorderRadius(
            borderRadius,
            borderRadius,
            borderRadius,
            borderRadius,
          );
        }
      });

      const targetNode = document.body;
      const configuration = {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      };
      observer.observe(targetNode, configuration);
    }
  }

  selectionMade(event: Event, trigger: MatAutocompleteTrigger) {
    if (this.showAddressCompliment) {
      event.stopPropagation();
      trigger.openPanel();
    }
  }

  onSubmit() {
    this.trigger?.closePanel();
    this.isFilled = false;
    this.isActive = false;
    this.submitted.emit();
  }
}
